import { useParams } from "react-router";
import {
  useQpmOfferPutGeneralTextBlocksMutation,
  useQpmOfferShowQuery,
} from "@/services/backend/qpm/offers/api/offer-api";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Separator } from "@/shared/components/ui/separator";
import {
  GeneralTextBlocks,
  PutGeneralTextBlocksRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { useEffect, useState } from "react";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "@/shared/components/editor/editor";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Check, Pencil, X } from "lucide-react";

export function TextBlocksRoute() {
  const params = useParams();
  const { offerId } = params;
  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    offer && (
      <Card className="">
        <CardHeader className="py-4 font-bold">
          <div className="flex items-center justify-between">
            <CardTitle>
              {t("Angebot")}
              {" - "}
              <DocumentNumberById className="text-xl" documentId={offer.id} />
            </CardTitle>
          </div>
        </CardHeader>
        <div className="mx-6 mb-2">
          <Separator />
        </div>
        <CardContent>
          <GeneralTextBlocksComponent
            offerId={offer.id}
            generalTextBlocks={offer.generalTextBlocks}
            disabled={offer.immutable}
          />
        </CardContent>
      </Card>
    )
  );
}

type CoverLetterTextBoxComponentProps = {
  offerId: string;
  generalTextBlocks: GeneralTextBlocks;
  disabled?: boolean;
  fieldErrors?: Record<string, string[]>;
  resetFieldError?: (field: string) => void;
};

type RichTextField = "currentState" | "offerClaimText";

function GeneralTextBlocksComponent({
  offerId,
  generalTextBlocks,
  disabled,
  fieldErrors,
  resetFieldError,
}: CoverLetterTextBoxComponentProps) {
  const [request, setRequest] = useState<PutGeneralTextBlocksRequest>({
    ...generalTextBlocks,
    offerId,
  });
  const [editingField, setEditingField] = useState<RichTextField | null>(null);

  // Keep local draft in sync when offer data changes.
  useEffect(() => {
    setRequest({
      ...generalTextBlocks,
      offerId,
    });
    setEditingField(null);
  }, [generalTextBlocks, offerId]);

  const [putText, { error, isLoading, isSuccess, reset }] =
    useQpmOfferPutGeneralTextBlocksMutation();

  const { onTrigger: triggerSave } = usePersistenceStateUpdateOnTrigger(
    request,
    putText,
    isLoading,
    error,
    isSuccess,
    reset,
    { toastError: true, toastSuccess: true },
  );

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [isSuccess, reset]);

  const hasAnyFieldErrors = (errs?: Record<string, string[]>) =>
    !!errs && Object.values(errs).some((v) => (v?.length ?? 0) > 0);

  const isEmpty =
    (request.currentState ?? "").trim() === "" &&
    (request.offerClaimText ?? "").trim() === "";

  const handleSaveField = (field: RichTextField) => {
    if (disabled) {
      return;
    }
    resetFieldError?.(field);
    triggerSave();
    setEditingField(null);
  };

  const handleCancelField = (field: RichTextField) => {
    setRequest((prev) => ({
      ...prev,
      [field]: generalTextBlocks[field] ?? "",
    }));
    setEditingField(null);
  };

  return (
    <div className="flex flex-col gap-4">
      {isEmpty && hasAnyFieldErrors(fieldErrors) ? (
        <div className="text-destructive text-sm">
          {Object.entries(fieldErrors ?? {})
            .flatMap(([field, errs]) =>
              (errs ?? []).map((msg) => ({ field, msg })),
            )
            .map(({ field, msg }) => (
              <div key={`${field}:${msg}`}>
                <span className="font-semibold">{field}:</span> {msg}
              </div>
            ))}
        </div>
      ) : null}

      <RichTextSection
        title={t("IST - STAND")}
        value={request.currentState}
        isEditing={editingField === "currentState"}
        isDisabled={disabled || isLoading}
        errorMessages={fieldErrors?.currentState}
        onStartEdit={() => setEditingField("currentState")}
        onChange={(val) => {
          resetFieldError?.("currentState");
          setRequest((prev) => ({ ...prev, currentState: val }));
        }}
        onSave={() => handleSaveField("currentState")}
        onCancel={() => handleCancelField("currentState")}
      />

      <RichTextSection
        title={t("Angebotsdisclaimer")}
        value={request.offerClaimText}
        isEditing={editingField === "offerClaimText"}
        isDisabled={disabled || isLoading}
        errorMessages={fieldErrors?.offerClaimText}
        onStartEdit={() => setEditingField("offerClaimText")}
        onChange={(val) => {
          resetFieldError?.("offerClaimText");
          setRequest((prev) => ({ ...prev, offerClaimText: val }));
        }}
        onSave={() => handleSaveField("offerClaimText")}
        onCancel={() => handleCancelField("offerClaimText")}
      />

      <div className="flex items-center gap-2">
        <Checkbox
          checked={request.displayOfferClaim}
          disabled={disabled || isLoading}
          onCheckedChange={(value) => {
            if (disabled) {
              return;
            }
            resetFieldError?.("displayOfferClaim");
            setRequest((prev) => ({ ...prev, displayOfferClaim: !!value }));
          }}
        />
        <Label>{t("Angebotsdisclaimer anzeigen")}</Label>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || isLoading}
          onClick={triggerSave}
        >
          {t("Speichern")}
        </Button>
      </div>
      {fieldErrors?.displayOfferClaim?.length ? (
        <p className="text-destructive text-sm">
          {fieldErrors.displayOfferClaim.join(" ")}
        </p>
      ) : null}

      {error && <RTKQueryErrorAlert error={error} />}
    </div>
  );
}

function RichTextSection({
  title,
  value,
  isEditing,
  isDisabled,
  errorMessages,
  onStartEdit,
  onChange,
  onSave,
  onCancel,
}: {
  title: string;
  value: string;
  isEditing: boolean;
  isDisabled?: boolean;
  errorMessages?: string[];
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-lg">{title}</Label>
        <TooltipProvider>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={onSave}
                    disabled={isDisabled}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Speichern")}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="outline" onClick={onCancel}>
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Abbrechen")}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={onStartEdit}
                  disabled={isDisabled}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("Bearbeiten")}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>

      <TipTapHtmlEditor
        value={value}
        onChange={onChange}
        disabled={isDisabled}
        editable={isEditing}
        showToolbar={isEditing}
      />

      {errorMessages?.length ? (
        <p className="text-destructive text-sm">{errorMessages.join(" ")}</p>
      ) : null}
    </div>
  );
}

function TipTapHtmlEditor({
  value,
  onChange,
  disabled,
  editable = true,
  showToolbar = editable,
}: {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  editable?: boolean;
  showToolbar?: boolean;
}) {
  const isEditable = editable && !disabled;
  const targetValue = value?.trim() ? value : "<p></p>";

  const editor = useEditor(
    {
      content: value,
      editable: isEditable,
      onUpdate: ({ editor: instance }) => {
        if (instance.isEditable && onChange) {
          onChange(editorGenerateHTML(instance));
        }
      },
    },
    [],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(isEditable, false);
  }, [editor, isEditable]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editorGenerateHTML(editor).trim() || "<p></p>";
    if (current === targetValue) {
      return;
    }
    if (isEditable && editor.isFocused) {
      return;
    }
    editor.commands.setContent(targetValue, { emitUpdate: false });
  }, [editor, isEditable, targetValue]);

  if (!editor) {
    return null;
  }

  return (
    <Editor editor={editor}>
      {showToolbar ? (
        <EditorToolbar>
          <EditorBoldItalicUnderlineGroup />
          <EditorToolbarSeparator />
          <EditorBulletList />
          <EditorOrderedList />
        </EditorToolbar>
      ) : null}
      <EditorContent />
    </Editor>
  );
}
